/**
 * Tours guiados e descrições breves das páginas de Biblioteca, Comunidade e Pessoal (+ /reports),
 * nos 10 idiomas nativos. Mesmo formato de studyTours.ts.
 * Ordem dos idiomas em d() e s(): pt, en, es, fr, it, de, hu, ja, zh, ru.
 */
import { TOUR_LANGS, TITLE, replayStep, Localized, Pair, StudyTourDef, TourStepDef } from './studyTours';

export type AppTourKey =
  | 'library' | 'online_library' | 'audiobooks'
  | 'friends' | 'chat' | 'community_feed' | 'book_club'
  | 'profile' | 'level_test' | 'reports' | 'achievements' | 'settings';

const d = (...v: string[]) => Object.fromEntries(TOUR_LANGS.map((l, i) => [l, v[i]])) as Localized<string>;
const s = (target: string, ...pairs: Pair[]): TourStepDef => ({
  target,
  text: Object.fromEntries(TOUR_LANGS.map((l, i) => [l, pairs[i]])) as Localized<Pair>,
});

// Atalhos para as páginas irmãs da Comunidade (mesmo texto em Amigos, Chat, Feed e Clube)
const communityLinks = s('[data-tour="community-links"]',
  ['Atalhos da comunidade', 'Pule direto para o feed, os amigos, o chat ou o clube do livro.'],
  ['Community shortcuts', 'Jump straight to the feed, friends, chat or book club.'],
  ['Atajos de la comunidad', 'Ve directo al feed, a tus amigos, al chat o al club de lectura.'],
  ['Raccourcis communauté', 'Accédez directement au fil, aux amis, au chat ou au club de lecture.'],
  ['Scorciatoie della community', 'Vai subito al feed, agli amici, alla chat o al club del libro.'],
  ['Community-Abkürzungen', 'Spring direkt zum Feed, zu Freunden, zum Chat oder zum Buchclub.'],
  ['Közösségi gyorsgombok', 'Ugorj egyenesen a hírfolyamra, a barátokhoz, a csevegéshez vagy a könyvklubhoz.'],
  ['コミュニティのショートカット', 'フィード、友達、チャット、読書クラブへすぐに移動できます。'],
  ['社区快捷入口', '直接前往动态、好友、聊天或读书会。'],
  ['Быстрые ссылки сообщества', 'Сразу переходите в ленту, к друзьям, в чат или книжный клуб.'],
);

export const APP_TOURS: Record<AppTourKey, StudyTourDef> = {
  // ── BIBLIOTECA ──────────────────────────────────────────────
  library: {
    description: d(
      'Seus livros importados, prontos para ler e estudar.',
      'Your imported books, ready to read and study.',
      'Tus libros importados, listos para leer y estudiar.',
      'Vos livres importés, prêts à être lus et étudiés.',
      'I tuoi libri importati, pronti da leggere e studiare.',
      'Deine importierten Bücher, bereit zum Lesen und Lernen.',
      'Az importált könyveid, olvasásra és tanulásra készen.',
      'インポートした本。すぐに読んで学べます。',
      '你导入的书籍，随时可以阅读和学习。',
      'Ваши импортированные книги, готовые к чтению и учёбе.',
    ),
    steps: [
      s(TITLE,
        ['Minha estante', 'Aqui ficam todos os livros que você trouxe para o LibriTui.'],
        ['My shelf', 'All the books you brought into LibriTui live here.'],
        ['Mi estantería', 'Aquí están todos los libros que trajiste a LibriTui.'],
        ['Ma bibliothèque', 'Tous les livres que vous avez ajoutés à LibriTui sont ici.'],
        ['La mia libreria', 'Qui trovi tutti i libri che hai portato su LibriTui.'],
        ['Mein Regal', 'Hier liegen alle Bücher, die du in LibriTui geholt hast.'],
        ['A polcom', 'Itt van minden könyv, amit a LibriTuiba hoztál.'],
        ['マイ本棚', 'LibriTui に追加したすべての本がここにあります。'],
        ['我的书架', '你添加到 LibriTui 的所有书都在这里。'],
        ['Моя полка', 'Здесь все книги, которые вы добавили в LibriTui.'],
      ),
      s('[data-tour="library-links"]',
        ['Mais para ler e ouvir', 'Abra os audiobooks ou a biblioteca online para encontrar novos livros grátis.'],
        ['More to read and hear', 'Open audiobooks or the online library to find new free books.'],
        ['Más para leer y escuchar', 'Abre los audiolibros o la biblioteca en línea para encontrar libros gratis.'],
        ['Plus à lire et à écouter', 'Ouvrez les livres audio ou la bibliothèque en ligne pour trouver des livres gratuits.'],
        ['Altro da leggere e ascoltare', 'Apri gli audiolibri o la biblioteca online per trovare nuovi libri gratis.'],
        ['Mehr zum Lesen und Hören', 'Öffne Hörbücher oder die Online-Bibliothek, um neue kostenlose Bücher zu finden.'],
        ['Még több olvasnivaló', 'Nyisd meg a hangoskönyveket vagy az online könyvtárat új, ingyenes könyvekért.'],
        ['読む・聴くをもっと', 'オーディオブックやオンライン図書館で、新しい無料の本を探せます。'],
        ['更多可读可听', '打开有声书或在线图书馆，寻找新的免费书籍。'],
        ['Ещё больше книг', 'Откройте аудиокниги или онлайн-библиотеку, чтобы найти новые бесплатные книги.'],
      ),
      s('#tour-search-btn',
        ['Buscar', 'Encontre um livro pelo título ou autor.'],
        ['Search', 'Find a book by title or author.'],
        ['Buscar', 'Encuentra un libro por título o autor.'],
        ['Rechercher', 'Trouvez un livre par titre ou auteur.'],
        ['Cerca', 'Trova un libro per titolo o autore.'],
        ['Suchen', 'Finde ein Buch nach Titel oder Autor.'],
        ['Keresés', 'Keress könyvet cím vagy szerző alapján.'],
        ['検索', 'タイトルや著者で本を探せます。'],
        ['搜索', '按书名或作者查找书籍。'],
        ['Поиск', 'Найдите книгу по названию или автору.'],
      ),
      s('#tour-more-btn',
        ['Organizar', 'Filtre por favoritos ou em leitura, mude a ordem e alterne entre grade e lista.'],
        ['Organize', 'Filter by favorites or in progress, change the order and switch grid or list.'],
        ['Organizar', 'Filtra por favoritos o en lectura, cambia el orden y alterna entre cuadrícula y lista.'],
        ['Organiser', 'Filtrez par favoris ou en cours, changez l’ordre et basculez grille ou liste.'],
        ['Organizza', 'Filtra per preferiti o in lettura, cambia l’ordine e passa da griglia a elenco.'],
        ['Ordnen', 'Filtere nach Favoriten oder laufend, ändere die Reihenfolge, wechsle Raster oder Liste.'],
        ['Rendezés', 'Szűrj kedvencekre vagy olvasás alattiakra, válts sorrendet, rácsot vagy listát.'],
        ['整理', 'お気に入りや読書中で絞り込み、並び順やグリッド／リスト表示を切り替えます。'],
        ['整理', '按收藏或在读筛选，调整排序，并切换网格或列表。'],
        ['Порядок', 'Фильтр по избранному или читаемому, сортировка и вид сеткой или списком.'],
      ),
      s('[data-tour="library-shelf"]',
        ['Seus livros', 'Toque em um livro para abrir o leitor e continuar de onde parou.'],
        ['Your books', 'Tap a book to open the reader and continue where you left off.'],
        ['Tus libros', 'Toca un libro para abrir el lector y seguir donde lo dejaste.'],
        ['Vos livres', 'Touchez un livre pour ouvrir le lecteur et reprendre où vous en étiez.'],
        ['I tuoi libri', 'Tocca un libro per aprire il lettore e riprendere da dove eri rimasto.'],
        ['Deine Bücher', 'Tippe auf ein Buch, um den Reader zu öffnen und weiterzulesen.'],
        ['A könyveid', 'Koppints egy könyvre, és folytasd ott, ahol abbahagytad.'],
        ['あなたの本', '本をタップするとリーダーが開き、続きから読めます。'],
        ['你的书', '点击一本书即可打开阅读器，从上次的位置继续。'],
        ['Ваши книги', 'Нажмите на книгу, чтобы продолжить чтение с того же места.'],
      ),
      replayStep,
    ],
  },

  online_library: {
    description: d(
      'Milhares de livros gratuitos para importar e ler no seu idioma de estudo.',
      'Thousands of free books to import and read in the language you study.',
      'Miles de libros gratis para importar y leer en el idioma que estudias.',
      'Des milliers de livres gratuits à importer et lire dans la langue étudiée.',
      'Migliaia di libri gratuiti da importare e leggere nella lingua che studi.',
      'Tausende kostenlose Bücher zum Importieren und Lesen in deiner Lernsprache.',
      'Több ezer ingyenes könyv, amit importálhatsz és a tanult nyelven olvashatsz.',
      '学習中の言語で読める、数千冊の無料の本。',
      '数千本免费书籍，可导入并用你学习的语言阅读。',
      'Тысячи бесплатных книг на изучаемом языке.',
    ),
    steps: [
      s(TITLE,
        ['Biblioteca online', 'Um acervo enorme de livros em domínio público, todos grátis.'],
        ['Online library', 'A huge collection of public-domain books, all free.'],
        ['Biblioteca en línea', 'Un enorme acervo de libros de dominio público, todos gratis.'],
        ['Bibliothèque en ligne', 'Une immense collection de livres du domaine public, tous gratuits.'],
        ['Biblioteca online', 'Un’enorme raccolta di libri di pubblico dominio, tutti gratis.'],
        ['Online-Bibliothek', 'Eine riesige Sammlung gemeinfreier Bücher, alle kostenlos.'],
        ['Online könyvtár', 'Hatalmas, közkincs könyvgyűjtemény, mind ingyenes.'],
        ['オンライン図書館', 'パブリックドメインの本の大きなコレクション。すべて無料です。'],
        ['在线图书馆', '海量公版书籍，全部免费。'],
        ['Онлайн-библиотека', 'Огромная коллекция общедоступных книг, все бесплатно.'],
      ),
      s('[data-tour="online-links"]',
        ['Atalhos', 'Volte para sua estante ou abra os audiobooks.'],
        ['Shortcuts', 'Go back to your shelf or open audiobooks.'],
        ['Atajos', 'Vuelve a tu estantería o abre los audiolibros.'],
        ['Raccourcis', 'Revenez à votre bibliothèque ou ouvrez les livres audio.'],
        ['Scorciatoie', 'Torna alla tua libreria o apri gli audiolibri.'],
        ['Abkürzungen', 'Zurück zu deinem Regal oder zu den Hörbüchern.'],
        ['Gyorsgombok', 'Vissza a polcodhoz vagy a hangoskönyvekhez.'],
        ['ショートカット', '本棚に戻るか、オーディオブックを開きます。'],
        ['快捷入口', '返回书架或打开有声书。'],
        ['Быстрые ссылки', 'Вернитесь на полку или откройте аудиокниги.'],
      ),
      s('#tour-library-search',
        ['Buscar livros', 'Digite um título ou autor para encontrar a obra.'],
        ['Search books', 'Type a title or author to find the book.'],
        ['Buscar libros', 'Escribe un título o autor para encontrar la obra.'],
        ['Chercher des livres', 'Tapez un titre ou un auteur pour trouver l’œuvre.'],
        ['Cerca libri', 'Scrivi un titolo o un autore per trovare l’opera.'],
        ['Bücher suchen', 'Gib einen Titel oder Autor ein, um das Werk zu finden.'],
        ['Könyvkeresés', 'Írj be egy címet vagy szerzőt a mű megtalálásához.'],
        ['本を検索', 'タイトルや著者を入力して作品を探します。'],
        ['搜索书籍', '输入书名或作者来查找作品。'],
        ['Поиск книг', 'Введите название или автора, чтобы найти произведение.'],
      ),
      s('[data-tour="online-filter"]',
        ['Filtrar por idioma', 'Mostre só os livros no idioma que você está aprendendo.'],
        ['Filter by language', 'Show only books in the language you are learning.'],
        ['Filtrar por idioma', 'Muestra solo los libros en el idioma que aprendes.'],
        ['Filtrer par langue', 'Affichez uniquement les livres dans la langue que vous apprenez.'],
        ['Filtra per lingua', 'Mostra solo i libri nella lingua che stai imparando.'],
        ['Nach Sprache filtern', 'Zeige nur Bücher in der Sprache, die du lernst.'],
        ['Szűrés nyelv szerint', 'Csak a tanult nyelvű könyvek jelenjenek meg.'],
        ['言語で絞り込み', '学習中の言語の本だけを表示します。'],
        ['按语言筛选', '只显示你正在学习的语言的书。'],
        ['Фильтр по языку', 'Показывать только книги на изучаемом языке.'],
      ),
      s('[data-tour="online-books"]',
        ['Escolha um livro', 'Toque para ver os detalhes e importá-lo para sua estante.'],
        ['Pick a book', 'Tap to see details and import it to your shelf.'],
        ['Elige un libro', 'Tócalo para ver los detalles e importarlo a tu estantería.'],
        ['Choisissez un livre', 'Touchez pour voir les détails et l’importer dans votre bibliothèque.'],
        ['Scegli un libro', 'Tocca per vedere i dettagli e importarlo nella tua libreria.'],
        ['Wähle ein Buch', 'Tippe für Details und importiere es in dein Regal.'],
        ['Válassz könyvet', 'Koppints a részletekért, és importáld a polcodra.'],
        ['本を選ぶ', 'タップして詳細を確認し、本棚に追加します。'],
        ['选择一本书', '点击查看详情并导入到书架。'],
        ['Выберите книгу', 'Нажмите, чтобы увидеть детали и добавить её на полку.'],
      ),
      replayStep,
    ],
  },

  audiobooks: {
    description: d(
      'Ouça livros narrados, com ou sem o texto sincronizado.',
      'Listen to narrated books, with or without synced text.',
      'Escucha libros narrados, con o sin el texto sincronizado.',
      'Écoutez des livres narrés, avec ou sans texte synchronisé.',
      'Ascolta libri narrati, con o senza testo sincronizzato.',
      'Höre vorgelesene Bücher, mit oder ohne synchronisierten Text.',
      'Hallgass felolvasott könyveket, szinkronizált szöveggel vagy anélkül.',
      'ナレーション付きの本を、同期テキストの有無を選んで聴けます。',
      '收听有声书，可选择是否同步显示文本。',
      'Слушайте книги с синхронным текстом или без него.',
    ),
    steps: [
      s(TITLE,
        ['Audiobooks', 'Treine o ouvido com histórias completas narradas por pessoas reais.'],
        ['Audiobooks', 'Train your ear with full stories narrated by real people.'],
        ['Audiolibros', 'Entrena el oído con historias completas narradas por personas reales.'],
        ['Livres audio', 'Entraînez votre oreille avec des histoires lues par de vraies personnes.'],
        ['Audiolibri', 'Allena l’orecchio con storie complete lette da persone vere.'],
        ['Hörbücher', 'Trainiere dein Gehör mit ganzen Geschichten, gelesen von echten Menschen.'],
        ['Hangoskönyvek', 'Edzd a füled valódi emberek által felolvasott történetekkel.'],
        ['オーディオブック', '実際の人が朗読する物語で、耳を鍛えましょう。'],
        ['有声书', '用真人朗读的完整故事训练听力。'],
        ['Аудиокниги', 'Тренируйте слух на историях, прочитанных живыми людьми.'],
      ),
      s('[data-tour="audiobooks-links"]',
        ['Atalhos', 'Vá para sua estante ou para a biblioteca online.'],
        ['Shortcuts', 'Go to your shelf or the online library.'],
        ['Atajos', 'Ve a tu estantería o a la biblioteca en línea.'],
        ['Raccourcis', 'Allez à votre bibliothèque ou à la bibliothèque en ligne.'],
        ['Scorciatoie', 'Vai alla tua libreria o alla biblioteca online.'],
        ['Abkürzungen', 'Zu deinem Regal oder zur Online-Bibliothek.'],
        ['Gyorsgombok', 'Irány a polcod vagy az online könyvtár.'],
        ['ショートカット', '本棚やオンライン図書館へ移動します。'],
        ['快捷入口', '前往书架或在线图书馆。'],
        ['Быстрые ссылки', 'Перейдите на полку или в онлайн-библиотеку.'],
      ),
      s('[data-tour="audiobooks-tabs"]',
        ['Dois acervos', 'Sincronizados mostram o texto acompanhando o áudio; o outro traz o acervo LibriVox.'],
        ['Two collections', 'Synced ones show the text following the audio; the other is the LibriVox collection.'],
        ['Dos colecciones', 'Los sincronizados muestran el texto junto al audio; la otra es la colección LibriVox.'],
        ['Deux collections', 'Les synchronisés affichent le texte avec l’audio ; l’autre est la collection LibriVox.'],
        ['Due raccolte', 'I sincronizzati mostrano il testo insieme all’audio; l’altra è la raccolta LibriVox.'],
        ['Zwei Sammlungen', 'Synchronisierte zeigen den Text zum Audio; die andere ist die LibriVox-Sammlung.'],
        ['Két gyűjtemény', 'A szinkronizáltaknál a szöveg követi a hangot; a másik a LibriVox gyűjtemény.'],
        ['2つのコレクション', '同期版は音声に合わせてテキストを表示。もう一方は LibriVox のコレクションです。'],
        ['两个书库', '同步版会随音频显示文本；另一个是 LibriVox 书库。'],
        ['Две коллекции', 'В синхронных текст идёт вместе с аудио; вторая коллекция из LibriVox.'],
      ),
      s('[data-tour="audiobooks-search"]',
        ['Buscar', 'Procure um audiobook pelo título ou autor.'],
        ['Search', 'Look for an audiobook by title or author.'],
        ['Buscar', 'Busca un audiolibro por título o autor.'],
        ['Rechercher', 'Cherchez un livre audio par titre ou auteur.'],
        ['Cerca', 'Cerca un audiolibro per titolo o autore.'],
        ['Suchen', 'Suche ein Hörbuch nach Titel oder Autor.'],
        ['Keresés', 'Keress hangoskönyvet cím vagy szerző szerint.'],
        ['検索', 'タイトルや著者でオーディオブックを探します。'],
        ['搜索', '按书名或作者查找有声书。'],
        ['Поиск', 'Ищите аудиокнигу по названию или автору.'],
      ),
      s('[data-tour="audiobooks-filter"]',
        ['Idioma', 'Escolha em qual idioma você quer ouvir.'],
        ['Language', 'Choose the language you want to listen in.'],
        ['Idioma', 'Elige en qué idioma quieres escuchar.'],
        ['Langue', 'Choisissez la langue d’écoute.'],
        ['Lingua', 'Scegli in quale lingua vuoi ascoltare.'],
        ['Sprache', 'Wähle die Sprache, in der du hören möchtest.'],
        ['Nyelv', 'Válaszd ki, milyen nyelven szeretnél hallgatni.'],
        ['言語', '聴きたい言語を選びます。'],
        ['语言', '选择你想收听的语言。'],
        ['Язык', 'Выберите язык прослушивания.'],
      ),
      s('[data-tour="audiobooks-list"]',
        ['Comece a ouvir', 'Toque em um audiobook para abrir os capítulos e o player.'],
        ['Start listening', 'Tap an audiobook to open its chapters and the player.'],
        ['Empieza a escuchar', 'Toca un audiolibro para abrir los capítulos y el reproductor.'],
        ['Commencez l’écoute', 'Touchez un livre audio pour ouvrir les chapitres et le lecteur.'],
        ['Inizia ad ascoltare', 'Tocca un audiolibro per aprire i capitoli e il lettore.'],
        ['Los geht’s', 'Tippe auf ein Hörbuch, um Kapitel und Player zu öffnen.'],
        ['Kezdd el', 'Koppints egy hangoskönyvre a fejezetekért és a lejátszóért.'],
        ['聴き始める', 'オーディオブックをタップすると、章とプレーヤーが開きます。'],
        ['开始收听', '点击有声书即可打开章节和播放器。'],
        ['Начните слушать', 'Нажмите на аудиокнигу, чтобы открыть главы и плеер.'],
      ),
      replayStep,
    ],
  },

  // ── COMUNIDADE ─────────────────────────────────────────────
  friends: {
    description: d(
      'Conecte-se com amigos e acompanhe quem estuda com você.',
      'Connect with friends and follow who studies with you.',
      'Conéctate con amigos y sigue a quienes estudian contigo.',
      'Connectez-vous avec des amis et suivez ceux qui étudient avec vous.',
      'Connettiti con gli amici e segui chi studia con te.',
      'Verbinde dich mit Freunden und sieh, wer mit dir lernt.',
      'Kapcsolódj barátokhoz, és kövesd, ki tanul veled.',
      '友達とつながり、一緒に学ぶ仲間を見守りましょう。',
      '与好友联系，关注和你一起学习的人。',
      'Добавляйте друзей и следите, кто учится вместе с вами.',
    ),
    steps: [
      s(TITLE,
        ['Amigos', 'Estudar acompanhado mantém a motivação em alta.'],
        ['Friends', 'Studying together keeps motivation high.'],
        ['Amigos', 'Estudiar acompañado mantiene alta la motivación.'],
        ['Amis', 'Étudier à plusieurs garde la motivation au top.'],
        ['Amici', 'Studiare in compagnia tiene alta la motivazione.'],
        ['Freunde', 'Gemeinsam lernen hält die Motivation hoch.'],
        ['Barátok', 'Együtt tanulni sokkal motiválóbb.'],
        ['友達', '誰かと一緒に学ぶと、やる気が続きます。'],
        ['好友', '结伴学习，动力满满。'],
        ['Друзья', 'Учиться вместе намного веселее.'],
      ),
      communityLinks,
      s('[data-tour="friends-connect"]',
        ['Adicionar amigo', 'Digite o @id de alguém para enviar um convite.'],
        ['Add a friend', 'Type someone’s @id to send an invitation.'],
        ['Añadir amigo', 'Escribe el @id de alguien para enviarle una invitación.'],
        ['Ajouter un ami', 'Saisissez l’@id d’une personne pour l’inviter.'],
        ['Aggiungi un amico', 'Scrivi l’@id di qualcuno per inviare un invito.'],
        ['Freund hinzufügen', 'Gib die @id einer Person ein, um sie einzuladen.'],
        ['Barát hozzáadása', 'Írd be valaki @azonosítóját a meghíváshoz.'],
        ['友達を追加', '相手の @id を入力して招待を送ります。'],
        ['添加好友', '输入对方的 @id 发送邀请。'],
        ['Добавить друга', 'Введите @id человека, чтобы отправить приглашение.'],
      ),
      s('[data-tour="friends-requests"]',
        ['Convites recebidos', 'Aceite ou recuse os pedidos de amizade que chegarem.'],
        ['Invitations received', 'Accept or decline incoming friend requests.'],
        ['Invitaciones recibidas', 'Acepta o rechaza las solicitudes de amistad.'],
        ['Invitations reçues', 'Acceptez ou refusez les demandes d’amis.'],
        ['Inviti ricevuti', 'Accetta o rifiuta le richieste di amicizia.'],
        ['Erhaltene Einladungen', 'Nimm Freundschaftsanfragen an oder lehne sie ab.'],
        ['Kapott meghívók', 'Fogadd el vagy utasítsd el a barátkéréseket.'],
        ['届いた招待', '友達リクエストを承認または拒否できます。'],
        ['收到的邀请', '接受或拒绝好友请求。'],
        ['Полученные приглашения', 'Принимайте или отклоняйте заявки в друзья.'],
      ),
      s('[data-tour="friends-list"]',
        ['Sua comunidade', 'Veja seus amigos, abra o perfil deles ou comece uma conversa.'],
        ['Your community', 'See your friends, open their profile or start a chat.'],
        ['Tu comunidad', 'Ve a tus amigos, abre su perfil o inicia una conversación.'],
        ['Votre communauté', 'Voyez vos amis, ouvrez leur profil ou lancez une discussion.'],
        ['La tua community', 'Vedi i tuoi amici, apri il loro profilo o inizia una chat.'],
        ['Deine Community', 'Sieh deine Freunde, öffne ihr Profil oder starte einen Chat.'],
        ['A közösséged', 'Nézd meg a barátaidat, a profiljukat, vagy kezdj csevegést.'],
        ['あなたのコミュニティ', '友達を確認し、プロフィールを開いたりチャットを始めたりできます。'],
        ['你的社区', '查看好友，打开他们的主页或开始聊天。'],
        ['Ваше сообщество', 'Смотрите друзей, открывайте профили или начинайте чат.'],
      ),
      replayStep,
    ],
  },

  chat: {
    description: d(
      'Converse com seus amigos e pratique o idioma por mensagens.',
      'Chat with your friends and practice the language by text.',
      'Habla con tus amigos y practica el idioma por mensajes.',
      'Discutez avec vos amis et pratiquez la langue par messages.',
      'Chatta con i tuoi amici e pratica la lingua per messaggi.',
      'Chatte mit Freunden und übe die Sprache per Nachricht.',
      'Csevegj a barátaiddal, és gyakorold a nyelvet üzenetekben.',
      '友達とメッセージで話し、言語を練習しましょう。',
      '和好友聊天，用消息练习语言。',
      'Переписывайтесь с друзьями и практикуйте язык.',
    ),
    steps: [
      s(TITLE,
        ['Conversas', 'Escrever para amigos é um ótimo treino de vocabulário real.'],
        ['Chats', 'Writing to friends is great real-world vocabulary practice.'],
        ['Conversaciones', 'Escribir a amigos es un gran entrenamiento de vocabulario real.'],
        ['Discussions', 'Écrire à des amis est un excellent entraînement de vocabulaire réel.'],
        ['Conversazioni', 'Scrivere agli amici è un ottimo allenamento di lessico reale.'],
        ['Chats', 'Freunden schreiben ist super Training für echten Wortschatz.'],
        ['Beszélgetések', 'A barátoknak írás remek valódi szókincsgyakorlás.'],
        ['チャット', '友達とのやり取りは、実践的な語彙の練習になります。'],
        ['聊天', '给好友写消息是很好的实用词汇练习。'],
        ['Чаты', 'Переписка с друзьями отлично тренирует живую лексику.'],
      ),
      communityLinks,
      s('[data-tour="chat-contacts"]',
        ['Contatos', 'Busque um amigo e escolha com quem quer conversar.'],
        ['Contacts', 'Search for a friend and choose who to talk to.'],
        ['Contactos', 'Busca a un amigo y elige con quién hablar.'],
        ['Contacts', 'Cherchez un ami et choisissez avec qui parler.'],
        ['Contatti', 'Cerca un amico e scegli con chi parlare.'],
        ['Kontakte', 'Suche einen Freund und wähle, mit wem du schreibst.'],
        ['Névjegyek', 'Keress rá egy barátra, és válaszd ki, kivel beszélsz.'],
        ['連絡先', '友達を検索して、話したい相手を選びます。'],
        ['联系人', '搜索好友，选择聊天对象。'],
        ['Контакты', 'Найдите друга и выберите, с кем общаться.'],
      ),
      s('[data-tour="chat-area"]',
        ['Mensagens', 'A conversa aparece aqui. Escreva no campo de baixo e envie.'],
        ['Messages', 'The conversation shows here. Type in the field below and send.'],
        ['Mensajes', 'La conversación aparece aquí. Escribe abajo y envía.'],
        ['Messages', 'La discussion s’affiche ici. Écrivez en bas et envoyez.'],
        ['Messaggi', 'La conversazione appare qui. Scrivi in basso e invia.'],
        ['Nachrichten', 'Hier erscheint der Chat. Unten schreiben und senden.'],
        ['Üzenetek', 'Itt jelenik meg a beszélgetés. Írj lent, és küldd el.'],
        ['メッセージ', '会話はここに表示されます。下の欄に入力して送信します。'],
        ['消息', '对话显示在这里。在下方输入并发送。'],
        ['Сообщения', 'Здесь появляется переписка. Пишите внизу и отправляйте.'],
      ),
      replayStep,
    ],
  },

  community_feed: {
    description: d(
      'Veja o que seus amigos e a comunidade estão lendo e compartilhando.',
      'See what your friends and the community are reading and sharing.',
      'Mira lo que tus amigos y la comunidad leen y comparten.',
      'Voyez ce que vos amis et la communauté lisent et partagent.',
      'Guarda cosa leggono e condividono i tuoi amici e la community.',
      'Sieh, was Freunde und Community lesen und teilen.',
      'Nézd meg, mit olvasnak és osztanak meg a barátaid és a közösség.',
      '友達やコミュニティが読んで共有しているものを見てみましょう。',
      '看看好友和社区正在读什么、分享什么。',
      'Смотрите, что читают и публикуют друзья и сообщество.',
    ),
    steps: [
      s(TITLE,
        ['Feed da comunidade', 'Citações e descobertas de leitura compartilhadas por outros estudantes.'],
        ['Community feed', 'Quotes and reading finds shared by other learners.'],
        ['Feed de la comunidad', 'Citas y hallazgos de lectura compartidos por otros estudiantes.'],
        ['Fil de la communauté', 'Citations et découvertes partagées par d’autres apprenants.'],
        ['Feed della community', 'Citazioni e scoperte condivise da altri studenti.'],
        ['Community-Feed', 'Zitate und Lesefunde anderer Lernender.'],
        ['Közösségi hírfolyam', 'Más tanulók által megosztott idézetek és olvasmányok.'],
        ['コミュニティフィード', '他の学習者が共有した引用や読書の発見です。'],
        ['社区动态', '其他学习者分享的语录和阅读发现。'],
        ['Лента сообщества', 'Цитаты и находки, которыми делятся другие ученики.'],
      ),
      communityLinks,
      s('[data-tour="feed-posts"]',
        ['Publicações', 'Toque em um post para ver os detalhes, reagir e comentar.'],
        ['Posts', 'Tap a post to see details, react and comment.'],
        ['Publicaciones', 'Toca una publicación para ver detalles, reaccionar y comentar.'],
        ['Publications', 'Touchez une publication pour voir, réagir et commenter.'],
        ['Post', 'Tocca un post per vedere i dettagli, reagire e commentare.'],
        ['Beiträge', 'Tippe auf einen Beitrag, um zu reagieren und zu kommentieren.'],
        ['Bejegyzések', 'Koppints egy bejegyzésre a részletekért, reakcióért, hozzászólásért.'],
        ['投稿', '投稿をタップすると、詳細の確認、リアクション、コメントができます。'],
        ['帖子', '点击帖子查看详情、互动和评论。'],
        ['Публикации', 'Нажмите на пост, чтобы открыть, отреагировать и прокомментировать.'],
      ),
      replayStep,
    ],
  },

  book_club: {
    description: d(
      'Leia o mesmo livro com amigos e debatam cada capítulo juntos.',
      'Read the same book with friends and discuss each chapter together.',
      'Lee el mismo libro con amigos y debatan cada capítulo juntos.',
      'Lisez le même livre entre amis et discutez chaque chapitre ensemble.',
      'Leggete lo stesso libro con gli amici e discutete ogni capitolo insieme.',
      'Lest dasselbe Buch mit Freunden und besprecht jedes Kapitel.',
      'Olvassátok ugyanazt a könyvet, és beszéljétek meg együtt a fejezeteket.',
      '友達と同じ本を読み、各章について一緒に語り合いましょう。',
      '和好友读同一本书，一起讨论每一章。',
      'Читайте одну книгу с друзьями и обсуждайте каждую главу.',
    ),
    steps: [
      s(TITLE,
        ['Clube do livro', 'Um grupo de até 5 pessoas lendo a mesma obra no mesmo ritmo.'],
        ['Book club', 'A group of up to 5 people reading the same book at the same pace.'],
        ['Club de lectura', 'Un grupo de hasta 5 personas leyendo la misma obra al mismo ritmo.'],
        ['Club de lecture', 'Un groupe de 5 personnes max. lisant le même livre au même rythme.'],
        ['Club del libro', 'Un gruppo fino a 5 persone che leggono la stessa opera allo stesso ritmo.'],
        ['Buchclub', 'Bis zu 5 Leute lesen dasselbe Buch im selben Tempo.'],
        ['Könyvklub', 'Legfeljebb 5 fős csoport, ugyanazt a művet azonos tempóban olvassa.'],
        ['読書クラブ', '最大5人で同じ本を同じペースで読むグループです。'],
        ['读书会', '最多 5 人以相同节奏阅读同一本书的小组。'],
        ['Книжный клуб', 'Группа до 5 человек читает одну книгу в одном темпе.'],
      ),
      communityLinks,
      s('[data-tour="club-roles"]',
        ['Criador ou participante', 'Crie um clube e escolha o livro, ou aceite o convite de um amigo.'],
        ['Creator or member', 'Create a club and choose the book, or accept a friend’s invite.'],
        ['Creador o participante', 'Crea un club y elige el libro, o acepta la invitación de un amigo.'],
        ['Créateur ou membre', 'Créez un club et choisissez le livre, ou acceptez l’invitation d’un ami.'],
        ['Creatore o membro', 'Crea un club e scegli il libro, o accetta l’invito di un amico.'],
        ['Gründer oder Mitglied', 'Gründe einen Club und wähle das Buch oder nimm eine Einladung an.'],
        ['Alapító vagy tag', 'Hozz létre klubot és válassz könyvet, vagy fogadj el egy meghívást.'],
        ['作成者か参加者か', 'クラブを作って本を選ぶか、友達からの招待に参加します。'],
        ['创建者或成员', '创建读书会并选书，或接受好友邀请。'],
        ['Создатель или участник', 'Создайте клуб и выберите книгу или примите приглашение друга.'],
      ),
      s('[data-tour="club-cta"]',
        ['Seus clubes', 'Veja os clubes de que você participa e crie um novo.'],
        ['Your clubs', 'See the clubs you belong to and create a new one.'],
        ['Tus clubes', 'Ve los clubes en los que participas y crea uno nuevo.'],
        ['Vos clubs', 'Voyez vos clubs et créez-en un nouveau.'],
        ['I tuoi club', 'Vedi i club a cui partecipi e creane uno nuovo.'],
        ['Deine Clubs', 'Sieh deine Clubs und gründe einen neuen.'],
        ['A klubjaid', 'Nézd meg a klubjaidat, és hozz létre újat.'],
        ['あなたのクラブ', '参加中のクラブを確認し、新しく作成できます。'],
        ['你的读书会', '查看你参加的读书会，或创建新的。'],
        ['Ваши клубы', 'Смотрите свои клубы и создавайте новые.'],
      ),
      replayStep,
    ],
  },

  // ── PESSOAL ─────────────────────────────────────────────────
  profile: {
    description: d(
      'Seu perfil público: posts, estante e amigos.',
      'Your public profile: posts, shelf and friends.',
      'Tu perfil público: publicaciones, estantería y amigos.',
      'Votre profil public : publications, bibliothèque et amis.',
      'Il tuo profilo pubblico: post, libreria e amici.',
      'Dein öffentliches Profil: Beiträge, Regal und Freunde.',
      'Nyilvános profilod: bejegyzések, polc és barátok.',
      '公開プロフィール：投稿、本棚、友達。',
      '你的公开主页：帖子、书架和好友。',
      'Ваш публичный профиль: посты, полка и друзья.',
    ),
    steps: [
      s(TITLE,
        ['Seu perfil', 'É assim que os outros estudantes veem você.'],
        ['Your profile', 'This is how other learners see you.'],
        ['Tu perfil', 'Así te ven los demás estudiantes.'],
        ['Votre profil', 'C’est ainsi que les autres apprenants vous voient.'],
        ['Il tuo profilo', 'Così ti vedono gli altri studenti.'],
        ['Dein Profil', 'So sehen dich andere Lernende.'],
        ['A profilod', 'Így látnak téged a többi tanulók.'],
        ['あなたのプロフィール', '他の学習者からはこのように見えます。'],
        ['你的主页', '其他学习者看到的你就是这样。'],
        ['Ваш профиль', 'Так вас видят другие ученики.'],
      ),
      s('[data-tour="profile-card"]',
        ['Cartão de apresentação', 'Avatar, nome, @id, níveis e conquista em destaque. A engrenagem abre os ajustes.'],
        ['Profile card', 'Avatar, name, @id, levels and featured achievement. The gear opens settings.'],
        ['Tarjeta de perfil', 'Avatar, nombre, @id, niveles y logro destacado. El engranaje abre los ajustes.'],
        ['Carte de profil', 'Avatar, nom, @id, niveaux et succès mis en avant. L’engrenage ouvre les réglages.'],
        ['Scheda profilo', 'Avatar, nome, @id, livelli e traguardo in evidenza. L’ingranaggio apre le impostazioni.'],
        ['Profilkarte', 'Avatar, Name, @id, Niveaus und Top-Erfolg. Das Zahnrad öffnet die Einstellungen.'],
        ['Profilkártya', 'Avatar, név, @azonosító, szintek, kiemelt eredmény. A fogaskerék a beállítások.'],
        ['プロフィールカード', 'アバター、名前、@id、レベル、注目の実績。歯車で設定を開きます。'],
        ['资料卡', '头像、名字、@id、等级和精选成就。齿轮可打开设置。'],
        ['Карточка профиля', 'Аватар, имя, @id, уровни и главное достижение. Шестерёнка открывает настройки.'],
      ),
      s('[data-tour="profile-counters"]',
        ['Contadores', 'Toque em posts, livros ou amigos para ir direto àquela aba.'],
        ['Counters', 'Tap posts, books or friends to jump to that tab.'],
        ['Contadores', 'Toca publicaciones, libros o amigos para ir a esa pestaña.'],
        ['Compteurs', 'Touchez publications, livres ou amis pour aller à l’onglet.'],
        ['Contatori', 'Tocca post, libri o amici per andare a quella scheda.'],
        ['Zähler', 'Tippe auf Beiträge, Bücher oder Freunde, um zum Tab zu springen.'],
        ['Számlálók', 'Koppints a bejegyzésekre, könyvekre vagy barátokra a fülhöz.'],
        ['カウンター', '投稿・本・友達をタップすると、そのタブへ移動します。'],
        ['计数', '点击帖子、书籍或好友，直接跳到对应标签。'],
        ['Счётчики', 'Нажмите на посты, книги или друзей, чтобы открыть вкладку.'],
      ),
      s('[data-tour="profile-tabs"]',
        ['Abas', 'Alterne entre posts, estante, amigos e itens salvos.'],
        ['Tabs', 'Switch between posts, shelf, friends and saved items.'],
        ['Pestañas', 'Cambia entre publicaciones, estantería, amigos y guardados.'],
        ['Onglets', 'Passez des publications à la bibliothèque, aux amis et aux favoris.'],
        ['Schede', 'Passa tra post, libreria, amici e salvati.'],
        ['Tabs', 'Wechsle zwischen Beiträgen, Regal, Freunden und Gespeichertem.'],
        ['Fülek', 'Válts a bejegyzések, polc, barátok és mentettek között.'],
        ['タブ', '投稿、本棚、友達、保存済みを切り替えます。'],
        ['标签', '在帖子、书架、好友和收藏之间切换。'],
        ['Вкладки', 'Переключайтесь между постами, полкой, друзьями и сохранённым.'],
      ),
      s('[data-tour="profile-content"]',
        ['Conteúdo', 'O que estiver na aba escolhida aparece aqui.'],
        ['Content', 'Whatever is in the chosen tab shows here.'],
        ['Contenido', 'Aquí aparece lo de la pestaña elegida.'],
        ['Contenu', 'Le contenu de l’onglet choisi s’affiche ici.'],
        ['Contenuto', 'Qui appare ciò che c’è nella scheda scelta.'],
        ['Inhalt', 'Hier erscheint der Inhalt des gewählten Tabs.'],
        ['Tartalom', 'Itt látszik a kiválasztott fül tartalma.'],
        ['コンテンツ', '選んだタブの内容がここに表示されます。'],
        ['内容', '所选标签的内容显示在这里。'],
        ['Содержимое', 'Здесь отображается выбранная вкладка.'],
      ),
      replayStep,
    ],
  },

  level_test: {
    description: d(
      'Descubra seu nível CEFR e o tamanho aproximado do seu vocabulário.',
      'Find out your CEFR level and the approximate size of your vocabulary.',
      'Descubre tu nivel MCER y el tamaño aproximado de tu vocabulario.',
      'Découvrez votre niveau CECRL et la taille approximative de votre vocabulaire.',
      'Scopri il tuo livello QCER e la dimensione approssimativa del tuo lessico.',
      'Finde dein GER-Niveau und die ungefähre Größe deines Wortschatzes heraus.',
      'Tudd meg a KER-szintedet és a szókincsed becsült méretét.',
      'CEFR レベルと、おおよその語彙数がわかります。',
      '了解你的 CEFR 等级和大致词汇量。',
      'Узнайте свой уровень CEFR и примерный словарный запас.',
    ),
    steps: [
      s(TITLE,
        ['Meu nível', 'Um teste rápido de vocabulário passivo, sem pegadinhas.'],
        ['My level', 'A quick passive-vocabulary test, no tricks.'],
        ['Mi nivel', 'Una prueba rápida de vocabulario pasivo, sin trampas.'],
        ['Mon niveau', 'Un test rapide de vocabulaire passif, sans pièges.'],
        ['Il mio livello', 'Un test rapido di lessico passivo, senza trabocchetti.'],
        ['Mein Niveau', 'Ein schneller Test des passiven Wortschatzes, ohne Tricks.'],
        ['A szintem', 'Gyors passzív szókincsteszt, csapdák nélkül.'],
        ['マイレベル', 'ひっかけなしの、受動語彙のクイックテストです。'],
        ['我的等级', '快速的被动词汇测试，没有陷阱。'],
        ['Мой уровень', 'Быстрый тест пассивного словаря, без подвохов.'],
      ),
      s('[data-tour="level-start"]',
        ['Novo teste', 'Escolha o idioma e marque só as palavras que você realmente conhece.'],
        ['New test', 'Choose the language and tick only the words you really know.'],
        ['Nueva prueba', 'Elige el idioma y marca solo las palabras que realmente conoces.'],
        ['Nouveau test', 'Choisissez la langue et cochez seulement les mots que vous connaissez vraiment.'],
        ['Nuovo test', 'Scegli la lingua e seleziona solo le parole che conosci davvero.'],
        ['Neuer Test', 'Wähle die Sprache und markiere nur Wörter, die du wirklich kennst.'],
        ['Új teszt', 'Válassz nyelvet, és csak a tényleg ismert szavakat jelöld.'],
        ['新しいテスト', '言語を選び、本当に知っている単語だけをチェックします。'],
        ['新测试', '选择语言，只勾选你真正认识的单词。'],
        ['Новый тест', 'Выберите язык и отмечайте только слова, которые точно знаете.'],
      ),
      s('[data-tour="level-history"]',
        ['Histórico', 'Seus resultados anteriores, para ver a evolução ao longo do tempo.'],
        ['History', 'Your previous results, to see your progress over time.'],
        ['Historial', 'Tus resultados anteriores, para ver tu evolución.'],
        ['Historique', 'Vos résultats précédents, pour suivre votre progression.'],
        ['Cronologia', 'I risultati precedenti, per vedere i tuoi progressi.'],
        ['Verlauf', 'Deine bisherigen Ergebnisse, um den Fortschritt zu sehen.'],
        ['Előzmények', 'Korábbi eredményeid, hogy lásd a fejlődést.'],
        ['履歴', '過去の結果から、成長の推移を確認できます。'],
        ['历史', '以往的结果，查看你的进步。'],
        ['История', 'Прошлые результаты, чтобы видеть прогресс.'],
      ),
      replayStep,
    ],
  },

  reports: {
    description: d(
      'Seu progresso em números: tempo, palavras, habilidades e erros.',
      'Your progress in numbers: time, words, skills and mistakes.',
      'Tu progreso en números: tiempo, palabras, habilidades y errores.',
      'Vos progrès en chiffres : temps, mots, compétences et erreurs.',
      'I tuoi progressi in numeri: tempo, parole, abilità ed errori.',
      'Dein Fortschritt in Zahlen: Zeit, Wörter, Fähigkeiten und Fehler.',
      'Fejlődésed számokban: idő, szavak, készségek és hibák.',
      '数字で見る進捗：時間、単語、スキル、ミス。',
      '用数字看进步：时间、单词、技能和错误。',
      'Ваш прогресс в цифрах: время, слова, навыки и ошибки.',
    ),
    steps: [
      s(TITLE,
        ['Relatórios', 'Tudo o que você estudou, organizado para você enxergar sua evolução.'],
        ['Reports', 'Everything you studied, organized so you can see your progress.'],
        ['Informes', 'Todo lo que estudiaste, organizado para ver tu evolución.'],
        ['Rapports', 'Tout ce que vous avez étudié, organisé pour voir vos progrès.'],
        ['Report', 'Tutto ciò che hai studiato, organizzato per vedere i progressi.'],
        ['Berichte', 'Alles, was du gelernt hast, übersichtlich für deinen Fortschritt.'],
        ['Jelentések', 'Minden, amit tanultál, rendezve, hogy lásd a fejlődést.'],
        ['レポート', '学習したすべてを整理し、成長が見えるようにしました。'],
        ['报告', '你学过的一切，整理好让你看见进步。'],
        ['Отчёты', 'Всё, что вы изучили, наглядно и по порядку.'],
      ),
      s('[data-tour="reports-filters"]',
        ['Período e idioma', 'Veja o dia, a semana, o mês ou o total, em um idioma ou em todos.'],
        ['Period and language', 'View the day, week, month or total, for one language or all.'],
        ['Periodo e idioma', 'Ve el día, la semana, el mes o el total, en un idioma o en todos.'],
        ['Période et langue', 'Affichez le jour, la semaine, le mois ou le total, pour une langue ou toutes.'],
        ['Periodo e lingua', 'Vedi giorno, settimana, mese o totale, per una lingua o tutte.'],
        ['Zeitraum und Sprache', 'Tag, Woche, Monat oder gesamt, für eine oder alle Sprachen.'],
        ['Időszak és nyelv', 'Nap, hét, hónap vagy összes, egy vagy minden nyelvre.'],
        ['期間と言語', '日・週・月・全期間を、言語ごとまたは全言語で表示します。'],
        ['时间段和语言', '按日、周、月或全部查看，可选单一语言或全部。'],
        ['Период и язык', 'День, неделя, месяц или всё время, по одному языку или всем.'],
      ),
      s('[data-tour="reports-overview"]',
        ['Retrospectiva', 'Um resumo rápido do período, com acesso à sua retrospectiva completa.'],
        ['Recap', 'A quick summary of the period, with access to your full recap.'],
        ['Retrospectiva', 'Un resumen rápido del periodo, con acceso a tu retrospectiva completa.'],
        ['Rétrospective', 'Un résumé rapide de la période, avec accès à votre rétrospective complète.'],
        ['Riepilogo', 'Un breve riepilogo del periodo, con accesso al riepilogo completo.'],
        ['Rückblick', 'Eine schnelle Zusammenfassung mit Zugang zum vollständigen Rückblick.'],
        ['Visszatekintés', 'Gyors összefoglaló az időszakról, a teljes visszatekintéssel.'],
        ['振り返り', '期間の簡単なまとめと、詳しい振り返りへの入口です。'],
        ['回顾', '该时段的快速总结，可查看完整回顾。'],
        ['Итоги', 'Краткая сводка за период и доступ к полным итогам.'],
      ),
      s('[data-tour="reports-detailed"]',
        ['Detalhes', 'Métricas por atividade e o fluxo diário de estudo em gráficos.'],
        ['Details', 'Metrics per activity and your daily study flow in charts.'],
        ['Detalles', 'Métricas por actividad y el flujo diario de estudio en gráficos.'],
        ['Détails', 'Indicateurs par activité et flux d’étude quotidien en graphiques.'],
        ['Dettagli', 'Metriche per attività e flusso di studio giornaliero in grafici.'],
        ['Details', 'Kennzahlen je Aktivität und dein täglicher Lernverlauf als Diagramm.'],
        ['Részletek', 'Tevékenységenkénti mutatók és a napi tanulás grafikonokon.'],
        ['詳細', 'アクティビティ別の指標と、日々の学習の流れをグラフで表示します。'],
        ['详情', '按活动的指标和每日学习流程图表。'],
        ['Подробности', 'Показатели по занятиям и ежедневная активность на графиках.'],
      ),
      s('[data-tour="reports-focus"]',
        ['Foco e diagnóstico', 'Suas habilidades mais fortes e os erros que ainda precisam de revisão.'],
        ['Focus and diagnosis', 'Your strongest skills and the mistakes that still need review.'],
        ['Foco y diagnóstico', 'Tus mejores habilidades y los errores que aún debes repasar.'],
        ['Focus et diagnostic', 'Vos meilleures compétences et les erreurs à revoir.'],
        ['Focus e diagnosi', 'Le tue abilità migliori e gli errori da ripassare.'],
        ['Fokus und Diagnose', 'Deine stärksten Fähigkeiten und Fehler, die noch Wiederholung brauchen.'],
        ['Fókusz és diagnózis', 'Legerősebb készségeid és az ismétlendő hibák.'],
        ['フォーカスと診断', '得意なスキルと、まだ復習が必要なミスです。'],
        ['重点与诊断', '你最擅长的技能和仍需复习的错误。'],
        ['Фокус и диагностика', 'Сильные навыки и ошибки, которые стоит повторить.'],
      ),
      replayStep,
    ],
  },

  achievements: {
    description: d(
      'Conquistas que você desbloqueia estudando todos os dias.',
      'Achievements you unlock by studying every day.',
      'Logros que desbloqueas estudiando cada día.',
      'Des succès à débloquer en étudiant chaque jour.',
      'Traguardi che sblocchi studiando ogni giorno.',
      'Erfolge, die du durch tägliches Lernen freischaltest.',
      'Eredmények, amiket napi tanulással oldasz fel.',
      '毎日の学習で解除できる実績です。',
      '每天学习就能解锁的成就。',
      'Достижения, которые открываются за ежедневную учёбу.',
    ),
    steps: [
      s(TITLE,
        ['Conquistas', 'Cada meta alcançada vira uma medalha na sua coleção.'],
        ['Achievements', 'Every goal you reach becomes a medal in your collection.'],
        ['Logros', 'Cada meta alcanzada se convierte en una medalla de tu colección.'],
        ['Succès', 'Chaque objectif atteint devient une médaille de votre collection.'],
        ['Traguardi', 'Ogni obiettivo raggiunto diventa una medaglia nella tua collezione.'],
        ['Erfolge', 'Jedes erreichte Ziel wird zu einer Medaille in deiner Sammlung.'],
        ['Eredmények', 'Minden elért cél érem lesz a gyűjteményedben.'],
        ['実績', '達成した目標は、コレクションのメダルになります。'],
        ['成就', '每个达成的目标都会变成你收藏中的一枚勋章。'],
        ['Достижения', 'Каждая цель превращается в медаль вашей коллекции.'],
      ),
      s('[data-tour="achievements-list"]',
        ['Sua coleção', 'Veja o que já conquistou e quanto falta para as próximas.'],
        ['Your collection', 'See what you have earned and how close the next ones are.'],
        ['Tu colección', 'Mira lo que ya lograste y cuánto falta para los siguientes.'],
        ['Votre collection', 'Voyez ce que vous avez obtenu et ce qu’il reste pour les suivants.'],
        ['La tua collezione', 'Guarda cosa hai ottenuto e quanto manca ai prossimi.'],
        ['Deine Sammlung', 'Sieh, was du erreicht hast und wie nah die nächsten sind.'],
        ['A gyűjteményed', 'Nézd meg, mit értél el, és mennyi van a következőkig.'],
        ['コレクション', '獲得した実績と、次の実績までの進み具合を確認できます。'],
        ['你的收藏', '查看已获得的成就以及距离下一个还有多远。'],
        ['Ваша коллекция', 'Смотрите полученное и сколько осталось до следующих.'],
      ),
      replayStep,
    ],
  },

  settings: {
    description: d(
      'Ajuste o app do seu jeito: idioma, tradução, sincronização e plano.',
      'Make the app yours: language, translation, sync and plan.',
      'Ajusta la app a tu gusto: idioma, traducción, sincronización y plan.',
      'Personnalisez l’app : langue, traduction, synchronisation et forfait.',
      'Personalizza l’app: lingua, traduzione, sincronizzazione e piano.',
      'Passe die App an: Sprache, Übersetzung, Synchronisierung und Tarif.',
      'Szabd magadra az appot: nyelv, fordítás, szinkron és előfizetés.',
      'アプリを自分好みに：言語、翻訳、同期、プラン。',
      '按你的方式设置应用：语言、翻译、同步和套餐。',
      'Настройте приложение: язык, перевод, синхронизация и тариф.',
    ),
    steps: [
      s(TITLE,
        ['Configurações', 'Tudo o que muda a forma como o LibriTui funciona para você.'],
        ['Settings', 'Everything that changes how LibriTui works for you.'],
        ['Ajustes', 'Todo lo que cambia cómo funciona LibriTui para ti.'],
        ['Réglages', 'Tout ce qui change le fonctionnement de LibriTui pour vous.'],
        ['Impostazioni', 'Tutto ciò che cambia come LibriTui funziona per te.'],
        ['Einstellungen', 'Alles, was ändert, wie LibriTui für dich funktioniert.'],
        ['Beállítások', 'Minden, ami a LibriTui működését alakítja.'],
        ['設定', 'LibriTui の動作をあなた向けに変える項目です。'],
        ['设置', '改变 LibriTui 使用方式的所有选项。'],
        ['Настройки', 'Всё, что меняет работу LibriTui под вас.'],
      ),
      s('[data-tour="settings-tabs"]',
        ['Seções', 'Preferências, tradução, sincronização, atualização, plano e ajuda.'],
        ['Sections', 'Preferences, translation, sync, update, plan and help.'],
        ['Secciones', 'Preferencias, traducción, sincronización, actualización, plan y ayuda.'],
        ['Sections', 'Préférences, traduction, synchronisation, mise à jour, forfait et aide.'],
        ['Sezioni', 'Preferenze, traduzione, sincronizzazione, aggiornamento, piano e aiuto.'],
        ['Bereiche', 'Präferenzen, Übersetzung, Synchronisierung, Update, Tarif und Hilfe.'],
        ['Szakaszok', 'Beállítások, fordítás, szinkron, frissítés, előfizetés és súgó.'],
        ['セクション', '設定、翻訳、同期、アップデート、プラン、ヘルプ。'],
        ['分区', '偏好、翻译、同步、更新、套餐和帮助。'],
        ['Разделы', 'Параметры, перевод, синхронизация, обновление, тариф и помощь.'],
      ),
      s('[data-tour="settings-panel"]',
        ['Ajustes da seção', 'As opções da seção escolhida aparecem aqui.'],
        ['Section options', 'The chosen section’s options show here.'],
        ['Opciones', 'Las opciones de la sección elegida aparecen aquí.'],
        ['Options', 'Les options de la section choisie s’affichent ici.'],
        ['Opzioni', 'Le opzioni della sezione scelta appaiono qui.'],
        ['Optionen', 'Die Optionen des gewählten Bereichs erscheinen hier.'],
        ['Beállítások', 'A választott szakasz opciói itt jelennek meg.'],
        ['オプション', '選んだセクションの項目がここに表示されます。'],
        ['选项', '所选分区的选项显示在这里。'],
        ['Параметры', 'Здесь отображаются параметры выбранного раздела.'],
      ),
      replayStep,
    ],
  },
};
